import React, { useState, useEffect } from 'react';
import {
  Box,
  Button,
  FormControl,
  FormLabel,
  Input,
  Select,
  VStack,
  Text,
  Card,
  CardBody,
  InputGroup,
  Checkbox,
  HStack,
  Tag,
  TagLabel,
  TagCloseButton,
  Wrap,
  WrapItem,
  Alert,
  AlertIcon,
  AlertTitle,
  AlertDescription,
} from '@chakra-ui/react';
import { MapPin, Plus } from 'lucide-react';
import emailjs from '@emailjs/browser';
import { US_STATES } from '../constants/location-data';
import { formatLocationInput } from '../services/locationService';
import { useIframeResize } from '../hooks/useIframeResize';
import { getRateLimitStatus, recordCalculation } from '../utils/rateLimiter';

const ROICalculator = () => {

  const [formData, setFormData] = useState({
    businessName: '',
    businessDescription: '',
    businessWebsite: '',
    state: '',
    customLocation: '',
    useCustomLocation: false,
    locations: [],
    monthlyBudget: '',
    averageOrderValue: ''
  });
  const [error, setError] = useState(null);
  const [locationFeedback, setLocationFeedback] = useState(null);
  const [isValidatingLocation, setIsValidatingLocation] = useState(false);
  const [rateLimitStatus, setRateLimitStatus] = useState(() => getRateLimitStatus());

  // Auto-adjust iframe height when content changes
  const updateParentHeight = useIframeResize([
    formData.locations, 
    formData.useCustomLocation, 
    formData.state, 
    error,
    locationFeedback,
    isValidatingLocation,
    rateLimitStatus
  ]);

  // Update rate limit status on component mount and periodically
  useEffect(() => {
    const updateStatus = () => {
      setRateLimitStatus(getRateLimitStatus());
    };
    
    updateStatus();
    
    // Update every minute to refresh countdown timer
    const interval = setInterval(updateStatus, 60000);
    
    return () => clearInterval(interval);
  }, []);

  const handleInputChange = async (e) => {
    const { name, value } = e.target;
    
    if (name === 'useCustomLocation') {
      setFormData(prev => ({
        ...prev,
        useCustomLocation: e.target.checked,
        customLocation: ''
      }));
      setLocationFeedback(null);
    } else if (name === 'customLocation' && value) {
      setFormData(prev => ({ ...prev, [name]: value }));
      if (formData.state) {
        setIsValidatingLocation(true);
        try {
          const validation = await formatLocationInput(value, formData.state);
          setLocationFeedback(validation);
          if (validation.isValid) {
            setFormData(prev => ({ 
              ...prev, 
              [name]: validation.formattedLocation 
            }));
          }
        } catch (error) {
          console.error('Location validation error:', error);
        }
        setIsValidatingLocation(false);
      }
    } else {
      setFormData(prev => ({ ...prev, [name]: value }));
    }
  };

  const addLocation = () => {
    const stateName = formData.state ? US_STATES.find(s => s.code === formData.state)?.name : null;
    let locationToAdd;

    if (formData.useCustomLocation && formData.customLocation) {
      locationToAdd = {
        state: formData.state,
        stateName,
        customLocation: formData.customLocation,
        displayName: `${formData.customLocation}, ${stateName || 'USA'}`,
        type: locationFeedback?.type || 'unknown'
      };
    } else if (formData.state) {
      locationToAdd = {
        state: formData.state,
        stateName,
        customLocation: null,
        displayName: `${stateName}, USA`,
        type: 'state'
      };
    }

    if (locationToAdd) {
      // Check if location already exists
      const exists = formData.locations.some(loc => 
        loc.state === locationToAdd.state && 
        loc.customLocation === locationToAdd.customLocation
      );

      if (!exists) {
        setFormData(prev => ({
          ...prev,
          locations: [...prev.locations, locationToAdd],
          // Reset location inputs
          state: '',
          customLocation: '',
          useCustomLocation: false
        }));
        setLocationFeedback(null);
        
        // Update iframe height after adding location
        setTimeout(updateParentHeight, 150);
      }
    }
  };

  const removeLocation = (index) => {
    setFormData(prev => ({
      ...prev,
      locations: prev.locations.filter((_, i) => i !== index)
    }));
    
    // Update iframe height after removing location
    setTimeout(updateParentHeight, 150);
  };

  const getLocationDisplayText = () => {
    if (formData.locations.length > 0) {
      return formData.locations.map(loc => loc.displayName).join(', ');
    }

    // If they're in the middle of configuring a location
    if (formData.state) {
      const stateName = US_STATES.find(s => s.code === formData.state)?.name;
      if (formData.useCustomLocation && formData.customLocation) {
        const feedback = locationFeedback;
        if (feedback?.formattedLocation) {
          return `${feedback.formattedLocation}, ${stateName}`;
        }
        return `${formData.customLocation}, ${stateName}`;
      }
      return `Entire ${stateName}`;
    }

    // Default case - entire US
    return 'Entire United States';
  };

  const validateForm = () => {
    // Check rate limiting first
    const currentStatus = getRateLimitStatus();
    if (!currentStatus.canCalculate) {
      const resetInfo = currentStatus.timeUntilReset 
        ? ` Please try again in ${currentStatus.timeUntilReset}.`
        : ' Please try again later.';
      setError(`You've reached the limit of 3 calculations per 24 hours.${resetInfo}`);
      return false;
    }

    if (!formData.businessDescription?.trim()) {
      setError('Please provide a business description');
      return false;
    }
    if (!formData.monthlyBudget || formData.monthlyBudget <= 0) {
      setError('Please enter a valid monthly budget');
      return false;
    }
    if (!formData.averageOrderValue || formData.averageOrderValue <= 0) {
      setError('Please enter a valid average order value');
      return false;
    }
    // If no locations are selected, add entire US as default
    if (formData.locations.length === 0) {
      setFormData(prev => ({
        ...prev,
        locations: [{
          state: 'US',
          stateName: 'United States',
          customLocation: null,
          displayName: 'United States',
          type: 'country'
        }]
      }));
    }
    return true;
  };

  const sendEmailNotification = async (formData) => {
    try {
      const templateParams = {
        business_name: formData.businessName || 'Not provided',
        business_description: formData.businessDescription,
        business_website: formData.businessWebsite || 'Not provided',
        target_locations: formData.locations.map(loc => loc.displayName).join(', '),
        monthly_budget: `$${parseFloat(formData.monthlyBudget || 0).toLocaleString()}`,
        average_order_value: `$${parseFloat(formData.averageOrderValue || 0).toLocaleString()}`,
        submission_time: new Date().toLocaleString(),
        location_details: formData.locations.map(loc => 
          `${loc.displayName} (Type: ${loc.type}${loc.state !== 'US' ? `, State: ${loc.stateName}` : ''})`
        ).join('\n')
      };

      await emailjs.send(
        'service_uh1t83e', // Service ID
        'service_uh1t83e', // Template ID
        templateParams,
        'XN3YgSOqjd-lyXulr' // Public Key
      );

      console.log('📧 Email notification sent successfully');
    } catch (error) {
      console.error('📧 Email notification failed:', error);
      // Don't block the user flow if email fails
    }
  };

  const handleSubmit = async (e) => {
    e.preventDefault();
    setError(null);
    
    // Validate form before proceeding
    if (!validateForm()) {
      return;
    }

    // Ensure we have locations set
    if (formData.locations.length === 0) {
      setFormData(prev => ({
        ...prev,
        locations: [{
          state: 'US',
          stateName: 'United States',
          customLocation: null,
          displayName: 'United States',
          type: 'country'
        }]
      }));
      // Wait for state update to complete
      await new Promise(resolve => setTimeout(resolve, 0));
    }
    
    // Record the calculation attempt before redirecting
    recordCalculation();
    setRateLimitStatus(getRateLimitStatus());

    // Send email notification (non-blocking)
    sendEmailNotification(formData).catch(console.error);

    // Just redirect to results page - let ResultsPage handle the API call
    handleAnalysisComplete();
  };

  const handleAnalysisComplete = () => {
    
    // Create the parameters for results page
    const params = new URLSearchParams({
      businessName: formData.businessName,
      businessDescription: formData.businessDescription,
      monthlyBudget: formData.monthlyBudget.replace(/[^0-9.]/g, ''), // Remove currency symbols
      averageOrderValue: formData.averageOrderValue.replace(/[^0-9.]/g, ''), // Remove currency symbols
      targetLocation: formData.locations[0]?.displayName || 'United States'
    });

    // Add website if available
    if (formData.businessWebsite) {
      params.append('website', formData.businessWebsite);
    }

    // Add all locations as JSON - the AI needs this for proper population calculations
    if (formData.locations && formData.locations.length > 0) {
      params.append('locations', JSON.stringify(formData.locations));
      
      // Also add primary location for backwards compatibility with URL display
      const primaryLocation = formData.locations[0];
      if (primaryLocation.state && primaryLocation.state !== 'US') {
        params.append('state', primaryLocation.state);
      }
      if (primaryLocation.customLocation) {
        params.append('city', primaryLocation.customLocation);
      }
    }
    
    console.log('🔄 Form submitted, preparing redirect...');
    console.log('🔗 Parameters:', params.toString());
    console.log('🖼️ In iframe?', window !== window.parent);
    
    // Check if we're in an iframe
    if (window !== window.parent) {
      // For iframe: redirect parent page to WordPress results page
      const baseUrl = process.env.REACT_APP_WEBSITE_URL || 'https://metricslab.ai';
      const redirectUrl = `${baseUrl}/google-ads/results/?${params.toString()}`;
      console.log('🚀 Redirecting parent to:', redirectUrl);
      window.parent.location.href = redirectUrl;
    } else {
      // For direct access: reload same page with parameters
      const redirectUrl = `?${params.toString()}`;
      console.log('🚀 Redirecting to:', redirectUrl);
      window.location.href = redirectUrl;
    }
  };



  // If we have an error, show it
  if (error) {
    return (
      <Box p={6} maxWidth="600px" mx="auto">
        <Card border="none" shadow="none">
          <CardBody p={8} textAlign="center">
            <Text color="red.500" fontSize="lg" fontWeight="bold">
              Error
            </Text>
            <Text color="gray.600">{error}</Text>
          </CardBody>
        </Card>
      </Box>
    );
  }

  // Otherwise, show the calculator form
  return (
    <>
      <Box p={6} maxWidth="600px" mx="auto">
        <Card border="none" shadow="none">
          <CardBody pt={8} p={0}>
            <VStack spacing={6} align="stretch">
              <form onSubmit={handleSubmit}>
                <VStack spacing={5}>
                  <FormControl>
                    <FormLabel fontSize="sm" fontWeight="semibold" color="gray.700">
                      Website URL (optional)
                    </FormLabel>
                    <InputGroup>
                      <Input
                        name="businessWebsite"
                        value={formData.businessWebsite}
                        onChange={handleInputChange}
                        placeholder="https://yourwebsite.com"
                        bg="#f7f7f7"
                        border="1px solid"
                        borderColor="gray.200"
                        _hover={{ borderColor: "gray.300" }}
                        _focus={{ borderColor: "blue.500", bg: "white" }}
                      />
                    </InputGroup>
                    <Text fontSize="xs" color="gray.500" mt={2}>
                      We analyze your website to understand your business model and target audience
                    </Text>
                  </FormControl>

                  <FormControl isRequired>
                    <FormLabel fontSize="sm" fontWeight="semibold" color="gray.700">
                      Business Description
                    </FormLabel>
                    <InputGroup>
                      <Input
                        name="businessDescription"
                        value={formData.businessDescription}
                        onChange={handleInputChange}
                        placeholder="Please write a short description of your business"
                        bg="#f7f7f7"
                        border="1px solid"
                        borderColor="gray.200"
                        _hover={{ borderColor: "gray.300" }}
                        _focus={{ borderColor: "blue.500", bg: "white" }}
                      />
                    </InputGroup>
                    <Text fontSize="xs" color="gray.500" mt={2}>
                      This helps our AI provide more accurate estimates based on your specific industry and business type
                    </Text>
                  </FormControl>

                  <FormControl>
                    <FormLabel fontSize="sm" fontWeight="semibold" color="gray.700">
                      Target Locations
                    </FormLabel>
                    
                    {/* Current targeting display */}
                    <Box 
                      p={4} 
                      bg="blue.50" 
                      borderRadius="md" 
                      border="1px solid" 
                      borderColor="blue.200"
                      mb={4}
                    >
                      <HStack spacing={2}>
                        <MapPin size={16} color="#3182ce" />
                        <Text fontSize="sm" fontWeight="medium" color="blue.700">
                          Currently targeting: {getLocationDisplayText()}
                        </Text>
                      </HStack>
                    </Box>

                    {/* Location refinement options */}
                    <VStack spacing={4} align="stretch">
                      <Text fontSize="sm" color="gray.600">
                        By default, we target the <strong>entire United States</strong>. You can optionally narrow down to specific states or cities:
                      </Text>

                      <Select
                        name="state"
                        value={formData.state}
                        onChange={handleInputChange}
                        placeholder="Select a specific state (optional)"
                        bg="#f7f7f7"
                        border="1px solid"
                        borderColor="gray.200"
                        _hover={{ borderColor: "gray.300" }}
                        _focus={{ borderColor: "blue.500", bg: "white" }}
                      >
                        {US_STATES.map(state => (
                          <option key={state.code} value={state.code}>
                            {state.name}
                          </option>
                        ))}
                      </Select>

                      {formData.state && (
                        <VStack spacing={3} align="stretch">
                          <Checkbox
                            name="useCustomLocation"
                            isChecked={formData.useCustomLocation}
                            onChange={handleInputChange}
                            colorScheme="blue"
                          >
                            Target specific city or area within {US_STATES.find(s => s.code === formData.state)?.name}
                          </Checkbox>

                          {formData.useCustomLocation && (
                            <VStack spacing={2} align="stretch">
                              <Input
                                name="customLocation"
                                value={formData.customLocation}
                                onChange={handleInputChange}
                                placeholder="Enter city, town, or area name"
                                bg="#f7f7f7"
                                border="1px solid"
                                borderColor={locationFeedback?.isValid === false ? "red.300" : "gray.200"}
                                _hover={{ borderColor: locationFeedback?.isValid === false ? "red.400" : "gray.300" }}
                                _focus={{ borderColor: locationFeedback?.isValid === false ? "red.500" : "blue.500", bg: "white" }}
                              />
                              {isValidatingLocation && (
                                <Text fontSize="sm" color="gray.500">
                                  Validating location...
                                </Text>
                              )}
                              {locationFeedback && !isValidatingLocation && (
                                <Text 
                                  fontSize="sm" 
                                  color={locationFeedback.isValid ? "green.500" : "red.500"}
                                >
                                  {locationFeedback.notes}
                                </Text>
                              )}
                            </VStack>
                          )}
                        </VStack>
                      )}

                      {(formData.state || formData.customLocation) && (
                        <HStack spacing={2}>
                          <Button
                            leftIcon={<Plus size={16} />}
                            onClick={addLocation}
                            isDisabled={!formData.state}
                            colorScheme="blue"
                            variant="outline"
                            size="sm"
                          >
                            Add This Location
                          </Button>
                          <Button
                            onClick={() => setFormData(prev => ({
                              ...prev,
                              state: '',
                              customLocation: '',
                              useCustomLocation: false
                            }))}
                            variant="ghost"
                            size="sm"
                            color="gray.500"
                          >
                            Clear
                          </Button>
                        </HStack>
                      )}

                      {formData.locations.length > 0 && (
                        <VStack spacing={2} align="stretch">
                          <Text fontSize="sm" fontWeight="medium" color="gray.700">
                            Added Locations:
                          </Text>
                          <Wrap spacing={2}>
                            {formData.locations.map((location, index) => (
                              <WrapItem key={index}>
                                <Tag 
                                  size="lg" 
                                  borderRadius="full" 
                                  variant="subtle" 
                                  colorScheme="blue"
                                >
                                  <TagLabel>
                                    <HStack spacing={1}>
                                      <MapPin size={14} />
                                      <Text>{location.displayName}</Text>
                                    </HStack>
                                  </TagLabel>
                                  <TagCloseButton onClick={() => removeLocation(index)} />
                                </Tag>
                              </WrapItem>
                            ))}
                          </Wrap>
                        </VStack>
                      )}
                    </VStack>
                  </FormControl>

                  <FormControl isRequired>
                    <FormLabel fontSize="sm" fontWeight="semibold" color="gray.700">
                      Monthly Advertising Budget (USD)
                    </FormLabel>
                    <InputGroup>
                      <Input
                        name="monthlyBudget"
                        type="number"
                        value={formData.monthlyBudget}
                        onChange={handleInputChange}
                        placeholder="5000"
                        bg="#f7f7f7"
                        border="1px solid"
                        borderColor="gray.200"
                        _hover={{ borderColor: "gray.300" }}
                        _focus={{ borderColor: "blue.500", bg: "white" }}
                      />
                    </InputGroup>
                    <Text fontSize="xs" color="gray.500" mt={2}>
                      Your monthly budget helps us calculate potential reach, clicks, and conversions
                    </Text>
                  </FormControl>

                  <FormControl isRequired>
                    <FormLabel fontSize="sm" fontWeight="semibold" color="gray.700">
                    Estimated Value Per Customer (USD)
                    </FormLabel>
                    <InputGroup>
                      <Input
                        name="averageOrderValue"
                        type="number"
                        value={formData.averageOrderValue}
                        onChange={handleInputChange}
                        placeholder="100"
                        bg="#f7f7f7"
                        border="1px solid"
                        borderColor="gray.200"
                        _hover={{ borderColor: "gray.300" }}
                        _focus={{ borderColor: "blue.500", bg: "white" }}
                      />
                    </InputGroup>
                    <Text fontSize="xs" color="gray.500" mt={2}>
                      This helps us calculate your potential revenue and return on investment (ROI)
                    </Text>
                  </FormControl>

                  {/* Rate Limit Indicator */}
                  {rateLimitStatus.remaining < 3 && (
                    <Alert status={!rateLimitStatus.canCalculate ? "warning" : "info"} borderRadius="md">
                      <AlertIcon />
                      <Box>
                        <AlertTitle fontSize="sm">
                          {!rateLimitStatus.canCalculate ? 'Daily Limit Reached' : 'Usage Status'}
                        </AlertTitle>
                        <AlertDescription fontSize="sm">
                          {!rateLimitStatus.canCalculate 
                            ? `You've used all 3 calculations today. ${rateLimitStatus.timeUntilReset ? `Resets in ${rateLimitStatus.timeUntilReset}` : 'Please try again later'}.`
                            : `${rateLimitStatus.remaining} calculations remaining today`
                          }
                        </AlertDescription>
                      </Box>
                    </Alert>
                  )}

                  {error && (
                    <Text color="red.500" fontSize="sm">
                      {error}
                    </Text>
                  )}

                  <Button
                    type="submit"
                    size="lg"
                    width="100%"
                    bgColor={!rateLimitStatus.canCalculate ? "gray.400" : "black"}
                    color="white"
                    fontSize="md"
                    _hover={{
                      shadow: !rateLimitStatus.canCalculate ? "none" : "xl"
                    }}
                    _active={{
                      transform: !rateLimitStatus.canCalculate ? "none" : "translateY(0)",
                    }}
                    transition="all 0.2s"
                    fontWeight="medium"
                    isLoading={false}
                    isDisabled={!rateLimitStatus.canCalculate}
                  >
                    {!rateLimitStatus.canCalculate 
                      ? `Daily Limit Reached${rateLimitStatus.timeUntilReset ? ` - Resets in ${rateLimitStatus.timeUntilReset}` : ''}` 
                      : `Calculate Potential Earnings${rateLimitStatus.remaining < 3 ? ` (${rateLimitStatus.remaining} left today)` : ''}`
                    }
                  </Button>
                  <Text fontSize="sm" color="black" mt={0} textAlign="center">
                    We do not store any of your data. It is only used to calculate your potential earnings.
                  </Text>
                </VStack>
              </form>
            </VStack>
          </CardBody>
        </Card>
      </Box>


    </>
  );
};

export default ROICalculator; 